import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, ScoreRing, scoreColor } from "@/components/common/ui";
import { cn } from "@/lib/utils";

const STATUS = {
  overdue: { c: "#F04438", label: "Overdue" },
  "due-soon": { c: "#F79009", label: "Due Soon" },
  "on-track": { c: "#12B76A", label: "On Track" },
};

export default function Compliance() {
  const [data, setData] = useState(null);
  const [fw, setFw] = useState("");

  useEffect(() => { api.get("/compliance").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;

  const items = fw ? data.requirements.filter((r) => r.framework === fw) : data.requirements;

  return (
    <div>
      <PageHeader overline="Governance · Regulatory Nervous System" title="Compliance & Regulatory Intelligence"
        description="Continuous monitoring across MAP, DDS 115 CMR, DPH, DMH, HIPAA, OSHA and licensing frameworks." />

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex items-center gap-4 rounded-xl border bg-card p-5 shadow-sm">
          <ScoreRing value={data.score} size={92} color={scoreColor(data.score)} />
          <div>
            <div className="overline">Compliance Score</div>
            <div className="text-xl font-bold" style={{ color: scoreColor(data.score) }}>{data.band}</div>
            <div className="text-xs text-muted-foreground">95+ Excellent · 90+ Good · 80+ At Risk · &lt;70 Critical</div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <div className="overline mb-2">Attention Required</div>
          <div className="flex gap-6">
            <div><div className="text-2xl font-bold text-[#F04438]">{data.requirements.filter((r) => r.status === "overdue").length}</div><div className="overline">Overdue</div></div>
            <div><div className="text-2xl font-bold text-[#F79009]">{data.requirements.filter((r) => r.status === "due-soon").length}</div><div className="overline">Due Soon</div></div>
            <div><div className="text-2xl font-bold text-[#12B76A]">{data.requirements.filter((r) => r.status === "on-track").length}</div><div className="overline">On Track</div></div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-5 shadow-sm">
          <div className="overline mb-2">Frameworks Monitored</div>
          <div className="flex flex-wrap gap-1.5">
            {data.frameworks.map((f) => <span key={f} className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium">{f}</span>)}
          </div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <button onClick={() => setFw("")} className={cn("rounded-full border px-3 py-1 text-xs font-medium", !fw ? "border-[#155EEF] bg-[#155EEF] text-white" : "bg-card")}>All</button>
        {data.frameworks.map((f) => (
          <button key={f} onClick={() => setFw(f)} className={cn("rounded-full border px-3 py-1 text-xs font-medium", fw === f ? "border-[#155EEF] bg-[#155EEF] text-white" : "bg-card")}>{f}</button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="careiq-scroll overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-4 py-3">Requirement</th><th className="px-4 py-3">Framework</th><th className="px-4 py-3">Program</th>
                <th className="px-4 py-3">Owner</th><th className="px-4 py-3">Due</th><th className="px-4 py-3">Score</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody className="divide-y">
              {items.map((c) => {
                const s = STATUS[c.status] || STATUS["on-track"];
                return (
                  <tr key={c.id} data-testid={`compliance-${c.id}`} className="transition-colors hover:bg-muted/40">
                    <td className="px-4 py-3 font-medium">{c.requirement}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.framework}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.program_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.owner}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.due_date}</td>
                    <td className="px-4 py-3 font-semibold" style={{ color: scoreColor(c.score) }}>{c.score}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold" style={{ color: s.c, backgroundColor: `${s.c}1a` }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: s.c }} />{s.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
